import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Building2, Loader2 } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminService } from '../services/admin-service';
import { RUBROS, type TipoDeEmpresa } from '../types/admin.types';

/**
 * Dar de alta una empresa cliente.
 *
 * 🔴 No existía. Una empresa **solo podía nacer por el formulario público de
 * registro**: para incorporar a un cliente había que registrarlo como si fuera un
 * desconocido que llegó a la web, y después entrar como él para armarle su gente.
 *
 * **El primer administrador se pide acá y no en un segundo paso.** Una empresa sin
 * nadie que pueda entrar no sirve para nada, y separarlo en dos pantallas es la
 * forma más fácil de terminar con empresas huérfanas que nadie sabe si borrar.
 */

interface EmpresaParecida {
  id: string;
  name: string;
  slug: string;
  archivada: boolean;
  motivos: Array<'NOMBRE' | 'NIT' | 'DOMINIO'>;
}

const MOTIVO_DE_PARECIDO = {
  NOMBRE: 'el nombre',
  NIT: 'el NIT',
  DOMINIO: 'el dominio del correo',
} as const;

interface NuevaEmpresaDialogProps {
  abierto: boolean;
  onClose: () => void;
  /** Se llama después de crear, para refrescar el listado. */
  onCreada: (tenantId: string) => void;
}

const VACIO = {
  name: '',
  // RETAIL por omisión: es lo que son todas las empresas de hoy.
  type: 'RETAIL' as TipoDeEmpresa,
  nit: '',
  email: '',
  phone: '',
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  adminPassword: '',
};

export function NuevaEmpresaDialog({ abierto, onClose, onCreada }: NuevaEmpresaDialogProps) {
  const { toast } = useToast();
  const [f, setF] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  // Empresas que se parecen a la que se está creando (aviso del servidor, no bloquea).
  const [parecidas, setParecidas] = useState<EmpresaParecida[] | null>(null);

  const claveCorta = f.adminPassword.length > 0 && f.adminPassword.length < 8;
  const listo =
    f.name.trim().length >= 2 &&
    f.adminFirstName.trim() &&
    f.adminLastName.trim() &&
    /\S+@\S+\.\S+/.test(f.adminEmail) &&
    f.adminPassword.length >= 8;

  /**
   * Editar nombre, NIT o correos cambia lo que se comparó: la confirmación de «es otra empresa»
   * ya no vale y el servidor tiene que volver a avisar con los datos nuevos.
   */
  function editar(cambio: Partial<typeof VACIO>) {
    setF({ ...f, ...cambio });
    setParecidas(null);
  }

  function cerrar() {
    setF(VACIO);
    setGuardando(false);
    setParecidas(null);
    onClose();
  }

  async function crear(confirmarParecida = false) {
    if (!listo) return;
    setGuardando(true);
    try {
      const { tenant, warning } = await adminService.createTenant({
        name: f.name.trim(),
        type: f.type,
        nit: f.nit.trim() || null,
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        ...(confirmarParecida ? { confirmarParecida: true } : {}),
        admin: {
          email: f.adminEmail.trim(),
          password: f.adminPassword,
          firstName: f.adminFirstName.trim(),
          lastName: f.adminLastName.trim(),
        },
      });
      toast({
        title: `${tenant.name} quedó creada`,
        // El aviso viene del servidor; se muestra tal cual porque es el matiz que
        // evita que alguien cree la empresa y espere un correo que no existe.
        description: warning ?? 'Ya podés asignarle plan y módulos.',
      });
      onCreada(tenant.id);
      cerrar();
    } catch (error: any) {
      // Aviso de empresa parecida: se muestra la lista y se deja seguir con confirmación.
      if (error?.response?.data?.codigo === 'EMPRESA_PARECIDA') {
        setParecidas(error.response.data.parecidas ?? []);
        setGuardando(false);
        return;
      }
      toast({
        variant: 'destructive',
        title: 'No se pudo crear la empresa',
        description: error?.response?.data?.error ?? 'Ocurrió un error.',
      });
      setGuardando(false);
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && cerrar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" aria-hidden="true" />
            Nueva empresa
          </DialogTitle>
          <DialogDescription>
            Se crea la empresa con su primer administrador, sus tipos de cotización y una prueba de
            15 días.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emp-nombre">Nombre de la empresa *</Label>
            <Input
              id="emp-nombre"
              value={f.name}
              onChange={(e) => editar({ name: e.target.value })}
              placeholder="ABCD Logistics"
            />
            <p className="text-xs text-muted-foreground">
              Es lo que va a ver su cliente en la cabecera de la cotización.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emp-rubro">Rubro *</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v as TipoDeEmpresa })}>
              <SelectTrigger id="emp-rubro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(RUBROS) as TipoDeEmpresa[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {RUBROS[k].etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/*
              Se explica qué cambia, porque el nombre del rubro no lo dice: no es una
              etiqueta administrativa, decide contra qué se compara esta empresa.
            */}
            <p className="text-xs text-muted-foreground">
              {f.type === 'COMIDA'
                ? 'Se compara contra restaurantes en aplicaciones de delivery, donde el precio de la app no es el que cobra el local.'
                : 'Se compara contra tiendas de ecommerce.'}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="emp-nit">NIT</Label>
              <Input id="emp-nit" value={f.nit} onChange={(e) => editar({ nit: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-tel">Teléfono</Label>
              <Input
                id="emp-tel"
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emp-mail">Correo de la empresa</Label>
            <Input
              id="emp-mail"
              type="email"
              value={f.email}
              onChange={(e) => editar({ email: e.target.value })}
              placeholder="contacto@abcd.bo"
            />
          </div>

          {/* ── El primer administrador ── */}
          <div className="space-y-4 rounded-md border border-border p-3">
            <div>
              <h4 className="text-sm font-semibold">Primer administrador</h4>
              <p className="text-xs text-muted-foreground">
                La persona que va a administrar esta empresa. Después ella crea al resto de su
                equipo.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="adm-nombre">Nombre *</Label>
                <Input
                  id="adm-nombre"
                  value={f.adminFirstName}
                  onChange={(e) => setF({ ...f, adminFirstName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adm-apellido">Apellido *</Label>
                <Input
                  id="adm-apellido"
                  value={f.adminLastName}
                  onChange={(e) => setF({ ...f, adminLastName: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adm-mail">Correo *</Label>
              <Input
                id="adm-mail"
                type="email"
                value={f.adminEmail}
                onChange={(e) => editar({ adminEmail: e.target.value })}
                placeholder="delicia@abcd.bo"
              />
              <p className="text-xs text-muted-foreground">
                Con este correo entra. No se puede repetir en toda la plataforma.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adm-clave">Contraseña *</Label>
              {/* En claro: quien crea la empresa se la tiene que dictar. */}
              <Input
                id="adm-clave"
                type="text"
                autoComplete="off"
                value={f.adminPassword}
                onChange={(e) => setF({ ...f, adminPassword: e.target.value })}
                placeholder="Al menos 8 caracteres"
                aria-invalid={claveCorta}
              />
              {claveCorta && (
                <p className="text-xs text-destructive">Tiene que tener al menos 8 caracteres.</p>
              )}
            </div>
          </div>

          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p>
              El sistema <strong>no le manda ningún correo</strong> a esta persona. La contraseña se
              la tenés que dar vos.
            </p>
          </div>
        </div>

        {parecidas !== null && (
          <div
            role="alert"
            className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
          >
            <p className="font-medium">Ya hay una empresa parecida. ¿Es la misma?</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              {parecidas.map((p) => (
                <li key={p.id}>
                  <strong>{p.name}</strong>
                  {p.archivada ? ' (archivada: quizá alcanza con restaurarla)' : ''} · coincide en{' '}
                  {p.motivos.map((m) => MOTIVO_DE_PARECIDO[m]).join(', ')}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Si es la misma, cancelá y usá la que ya existe. Si es otra distinta, creala igual.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={() => crear(parecidas !== null)} disabled={!listo || guardando}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {parecidas !== null ? 'Es otra empresa, crear igual' : 'Crear empresa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NuevaEmpresaDialog;
